/**
 * DisciplineSummarySheet — the Labour Planning Worksheet for one discipline,
 * scoped to the selected core estimate.
 *
 * Middle layer of the estimation flow:
 *   Detailed Estimation  ->  THIS SHEET  ->  CB Summary one-pager
 *
 * Rows tagged account_no = 'AUTO' are rolled up from the detail sheet and
 * refresh every time "Roll up" is run there; all other rows are manual
 * (support staff, supervision, or anything estimated at summary level).
 * Template rows can also be pulled in from the shared library.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useCbApi, useCbConfig } from './cbApi';
import {
  SheetShell, HeaderButton, ActionButton, EditableTable, HelpModal, LibraryPickerModal,
  LoadingState, ErrorState, EmptyState, NoEstimateState, useToast, downloadExport, fmt,
} from './CbUi';
import CbAuditLogButton from './CbAuditLogButton';

const DisciplineSummarySheet = ({ estimate, discipline, onOpenDetail }) => {
  const api = useCbApi();
  const { config, loading: configLoading } = useCbConfig();
  const { showToast, ToastEl } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeSection, setActiveSection] = useState('ALL');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [showHelp, setShowHelp] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [rollingUp, setRollingUp] = useState(false);

  const disc = useMemo(
    () => config?.disciplines.find((d) => d.key === discipline) || { label: discipline, code: '?', color: '#6b7280', icon: '📋' },
    [config, discipline]
  );
  // cb_discipline_summary stores the display label in its discipline column
  const discLabel = disc.label;

  const sectionMeta = useMemo(() => {
    const map = {};
    (config?.sections || []).forEach((s) => { map[s.key] = s; });
    return map;
  }, [config]);

  const columnsFor = useCallback((section) => {
    const type = sectionMeta[section]?.sectionType || 'labour';
    return config?.summaryColumns?.[type] || [];
  }, [config, sectionMeta]);

  const fetchRows = useCallback(async () => {
    if (!estimate?.id) return;
    try {
      setLoading(true);
      const res = await api.get(`/cost-breakdown/discipline-summary?estimate_id=${estimate.id}&discipline=${encodeURIComponent(discLabel)}`);
      const data = res.data?.data || [];
      setRows(data);
      setExpanded((prev) => (prev.size === 0 ? new Set(data.map((r) => r.section)) : prev));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, estimate?.id, discLabel]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filteredRows = useMemo(() => {
    let out = rows;
    if (activeSection !== 'ALL') out = out.filter((r) => r.section === activeSection);
    if (search) {
      const term = search.toLowerCase();
      out = out.filter((r) =>
        ['description', 'account_no', 'labour_type', 'notes', 'wbs_code', 'sov_code'].some(
          (k) => String(r[k] ?? '').toLowerCase().includes(term)
        )
      );
    }
    return out;
  }, [rows, activeSection, search]);

  const sectionGroups = useMemo(() => {
    const groups = {};
    filteredRows.forEach((r) => {
      (groups[r.section] = groups[r.section] || []).push(r);
    });
    return groups;
  }, [filteredRows]);

  const orderedSections = useMemo(() => {
    const order = (config?.sections || []).map((s) => s.key);
    const present = Object.keys(sectionGroups);
    return order.filter((s) => present.includes(s)).concat(present.filter((s) => !order.includes(s)));
  }, [config, sectionGroups]);

  const kpis = useMemo(() => {
    const live = rows.filter((r) => !r.is_total_row);
    const sum = (key) => live.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    return [
      { label: 'Rows', value: rows.length, icon: '📋', color: disc.color },
      { label: 'Man Hours', value: fmt(sum('man_hours'), 'number'), icon: '⏱️', color: '#059669' },
      { label: 'Dollar Cost', value: fmt(sum('dollar_cost'), 'currency'), icon: '💰', color: '#dc2626' },
      { label: 'R&B Cost', value: fmt(sum('total_rnb_cost'), 'currency'), icon: '🏨', color: '#ea580c' },
    ];
  }, [rows, disc.color]);

  const handleCellSave = useCallback(async (row, key, value) => {
    try {
      const res = await api.put(`/cost-breakdown/discipline-summary/${row.id}`, { [key]: value });
      const updated = res.data?.data;
      if (updated) setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
      else fetchRows();
    } catch (err) {
      showToast(`Update failed: ${err.message}`, 'error');
    }
  }, [api, fetchRows, showToast]);

  const handleAddRow = useCallback(async (section) => {
    try {
      const res = await api.post('/cost-breakdown/discipline-summary', {
        estimate_id: estimate.id,
        discipline: discLabel,
        discipline_code: disc.code,
        section,
        description: 'New row',
        sort_order: (rows.length + 1) * 10,
      });
      const created = res.data?.data;
      if (created) {
        setRows((prev) => [...prev, created]);
        setExpanded((prev) => new Set(prev).add(section));
      }
      showToast(`Row added to ${section}`);
    } catch (err) {
      showToast(`Failed to add row: ${err.message}`, 'error');
    }
  }, [api, estimate?.id, discLabel, disc.code, rows.length, showToast]);

  const handleRollFromDetail = useCallback(async () => {
    if (!estimate?.id || !discipline) return;
    setRollingUp(true);
    try {
      const res = await api.post('/cost-breakdown/discipline-summary/pull-from-detail', {
        estimate_id: estimate.id,
        discipline,
      });
      showToast(res.data?.message || 'Rolled up from Detailed Estimation');
      fetchRows();
    } catch (err) {
      showToast(err.response?.data?.message || err.message, 'error');
    } finally {
      setRollingUp(false);
    }
  }, [api, estimate?.id, discipline, fetchRows, showToast]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedRows.size === 0) return;
    if (!window.confirm(`Delete ${selectedRows.size} selected row(s)?`)) return;
    try {
      await api.post('/cost-breakdown/discipline-summary/bulk-delete', { ids: [...selectedRows] });
      setRows((prev) => prev.filter((r) => !selectedRows.has(r.id)));
      setSelectedRows(new Set());
      showToast('Deleted');
    } catch (err) {
      showToast(`Delete failed: ${err.message}`, 'error');
    }
  }, [api, selectedRows, showToast]);

  const handleExport = useCallback(async () => {
    try {
      await downloadExport(api, `/cost-breakdown/discipline-summary/export?estimate_id=${estimate.id}&discipline=${encodeURIComponent(discLabel)}`,
        `${estimate.estimate_number}_${discipline}_summary.xlsx`);
      showToast('Exported');
    } catch (err) {
      showToast(`Export failed: ${err.message}`, 'error');
    }
  }, [api, estimate, discipline, discLabel, showToast]);

  if (!estimate?.id) return <NoEstimateState section="Discipline Summary" />;
  if (configLoading || (loading && rows.length === 0)) return <LoadingState label={`Loading ${disc.label} summary…`} />;
  if (error) return <ErrorState error={error} onRetry={fetchRows} />;

  const renderSection = (section) => {
    const sectionRows = sectionGroups[section];
    const meta = sectionMeta[section] || { icon: '📋', color: '#6b7280' };
    const isOpen = expanded.has(section);
    const total = sectionRows.filter((r) => !r.is_total_row).reduce((s, r) => s + (Number(r.dollar_cost) || 0), 0);

    return (
      <div key={section} style={{ marginBottom: 16, border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div
          onClick={() => setExpanded((prev) => {
            const next = new Set(prev);
            next.has(section) ? next.delete(section) : next.add(section);
            return next;
          })}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '11px 18px', background: `linear-gradient(135deg, ${meta.color}, ${meta.color}cc)`,
            color: '#fff', cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{meta.icon}</span>
            <span style={{ fontSize: 14.5, fontWeight: 600 }}>{section}</span>
            <span style={{ fontSize: 11.5, opacity: 0.85, background: 'rgba(255,255,255,0.2)', padding: '2px 10px', borderRadius: 12 }}>
              {sectionRows.length} {sectionRows.length === 1 ? 'row' : 'rows'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {total > 0 && <span style={{ fontSize: 13.5, fontWeight: 600 }}>{fmt(total, 'currency')}</span>}
            <button
              onClick={(e) => { e.stopPropagation(); handleAddRow(section); }}
              style={{ background: 'rgba(255,255,255,0.22)', border: 'none', color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}
            >+ Row</button>
            <span style={{ fontSize: 16, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
          </div>
        </div>
        {isOpen && (
          <EditableTable
            columns={columnsFor(section)}
            rows={sectionRows}
            onCellSave={handleCellSave}
            selectedRows={selectedRows}
            onToggleRow={(id) => setSelectedRows((prev) => {
              const next = new Set(prev);
              next.has(id) ? next.delete(id) : next.add(id);
              return next;
            })}
            onToggleAll={() => setSelectedRows((prev) => {
              const ids = sectionRows.map((r) => r.id);
              const all = ids.every((id) => prev.has(id));
              const next = new Set(prev);
              ids.forEach((id) => (all ? next.delete(id) : next.add(id)));
              return next;
            })}
            accent={meta.color}
            rowStyle={(row) => (row.account_no === 'AUTO'
              ? { background: `${meta.color}0d`, borderLeft: `4px solid ${meta.color}` }
              : {})}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <SheetShell
        title={`${disc.icon} ${disc.label} — Discipline Summary`}
        subtitle={`Estimate ${estimate.estimate_number} · Labour Planning Worksheet · rolls up into the CB Summary`}
        gradient={`linear-gradient(135deg, ${disc.color}, ${disc.color}cc)`}
        headerActions={
          <>
            {onOpenDetail && <HeaderButton onClick={() => onOpenDetail(discipline)}>🔍 Open Detailed Estimation</HeaderButton>}
            <HeaderButton onClick={handleRollFromDetail} disabled={rollingUp} title="Re-roll AUTO rows from Detailed Estimation">
              {rollingUp ? '⏳ Rolling up…' : '⬆️ Re-roll from Detail'}
            </HeaderButton>
            <HeaderButton onClick={() => setShowLibrary(true)}>📚 Import from Library</HeaderButton>
            <HeaderButton onClick={handleExport}>📥 Export</HeaderButton>
            <CbAuditLogButton
              tableName="cb_discipline_summary"
              estimateId={estimate?.id}
              title={`${disc.label} Summary Audit Log`}
              accentColor={disc.color}
            />
            <HeaderButton onClick={() => setShowHelp(true)}>❓ Help</HeaderButton>
          </>
        }
        kpiCards={kpis}
        toolbar={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                placeholder="Search descriptions, accounts, codes…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex: '1 1 240px', maxWidth: 380, padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' }}
              />
              {selectedRows.size > 0 && (
                <ActionButton color="#dc2626" onClick={handleBulkDelete}>🗑️ Delete ({selectedRows.size})</ActionButton>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                onClick={() => setActiveSection('ALL')}
                style={{
                  padding: '5px 13px', borderRadius: 20, fontSize: 11.5, fontWeight: 600,
                  border: activeSection === 'ALL' ? `2px solid ${disc.color}` : '1px solid #d1d5db',
                  background: activeSection === 'ALL' ? disc.color : '#fff',
                  color: activeSection === 'ALL' ? '#fff' : '#4b5563', cursor: 'pointer',
                }}
              >All Sections</button>
              {(config?.sections || []).map((s) => {
                const has = rows.some((r) => r.section === s.key);
                if (!has && activeSection !== s.key) return null;
                const isActive = activeSection === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => setActiveSection(s.key)}
                    style={{
                      padding: '5px 11px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      border: isActive ? `2px solid ${s.color}` : '1px solid #e5e7eb',
                      background: isActive ? s.color : '#fff',
                      color: isActive ? '#fff' : '#6b7280', cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    <span>{s.icon}</span> {s.key}
                  </button>
                );
              })}
            </div>
          </div>
        }
      >
        {rows.length === 0 ? (
          <EmptyState icon={disc.icon} title={`No ${disc.label} summary rows yet`}>
            <p>Three ways to populate this worksheet:</p>
            <p>
              1. Fill the <strong>Detailed Estimation</strong> sheet, then click <strong>Roll up to Summary</strong> there.<br />
              2. <strong>Import from Library</strong> to pull in standard template rows.<br />
              3. Add rows manually with the <strong>+ Row</strong> button on any section header.
            </p>
            <div style={{ marginTop: 12 }}>
              <ActionButton color={disc.color} onClick={() => handleAddRow('Direct Labour')}>+ Add first row</ActionButton>
            </div>
          </EmptyState>
        ) : (
          orderedSections.map(renderSection)
        )}
        {ToastEl}
      </SheetShell>

      <LibraryPickerModal
        isOpen={showLibrary}
        onClose={() => setShowLibrary(false)}
        onImported={(count) => { showToast(`Imported ${count} template rows`); fetchRows(); }}
        resource="discipline-summary"
        estimateId={estimate.id}
        title="Discipline Summary Library"
        accent={disc.color}
        filterColumn="section"
        displayColumns={[
          { key: 'discipline', label: 'Discipline', type: 'text' },
          { key: 'section', label: 'Section', type: 'text' },
          { key: 'account_no', label: 'Account', type: 'text' },
          { key: 'description', label: 'Description', type: 'text' },
          { key: 'man_hours', label: 'Man Hours', type: 'number' },
          { key: 'total_rate', label: 'Rate', type: 'currency' },
          { key: 'dollar_cost', label: 'Cost', type: 'currency' },
        ]}
      />

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title={`${disc.label} Summary Help`} accent={disc.color} guideId="discipline-summary-help">
        <p><strong>This is the Labour Planning Worksheet</strong> for <strong>{disc.label}</strong> on estimate <strong>{estimate.estimate_number}</strong> — the middle layer between the detailed takeoff and the one-page CB Summary.</p>
        <h4>Where rows come from</h4>
        <ul style={{ paddingLeft: 20 }}>
          <li><strong>AUTO rows</strong> (highlighted, account <code>AUTO</code>) are rolled up from the Detailed Estimation sheet. Re-running the roll-up refreshes them in place.</li>
          <li><strong>Library imports</strong> bring in standard template rows (typical support staff, supervision structures…).</li>
          <li><strong>Manual rows</strong> — click <strong>+ Row</strong> on any section header for anything estimated at summary level.</li>
        </ul>
        <h4>Sections</h4>
        <ul style={{ paddingLeft: 20 }}>
          <li><strong>Project Support Staff / Supervisory Labour</strong> — PMs, engineers, QC, superintendents, foremen.</li>
          <li><strong>Direct / Other Direct Labour</strong> — the core construction hours.</li>
          <li><strong>Materials, Sub-Trades, Rentals, Inspection, R&amp;B, Travel, Meal, Site Expense</strong> — the non-labour cost buckets that feed the one-pager.</li>
        </ul>
        <h4>Working the sheet</h4>
        <ul style={{ paddingLeft: 20 }}>
          <li><strong>Click</strong> a cell to edit; totals refresh instantly in the KPI cards.</li>
          <li>Filter with the <strong>section pills</strong>, collapse sections by clicking their header.</li>
          <li>Everything here aggregates live into the <strong>CB Summary</strong> one-pager — no separate sync step.</li>
        </ul>
      </HelpModal>
    </>
  );
};

export default DisciplineSummarySheet;
